"use client";

import { ApiLogger } from "@/lib/logging/logger";
import { MEDIA_CONFIGS, UPLOAD_SETTINGS } from "@/lib/media/config";
import { MediaContext } from "@/lib/media/types";
import { validateFileType } from "@/lib/media/validation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status:
    | "pending"
    | "preparing"
    | "uploading"
    | "creating_record"
    | "completed"
    | "failed";
  error?: string;
}

export interface UploadSession {
  id: string;
  eventId: number;
  albumId?: number;
  context: MediaContext;
  files: UploadFile[];
  startedAt: Date;
  overallProgress: number;
  status: "uploading" | "completed" | "failed" | "partial";
}

export type UploadCompletionSummary = {
  totalFiles: number;
  uploadedFiles: number;
  failedFiles: number;
  status: UploadSession["status"];
};

interface UploadContextValue {
  sessions: UploadSession[];
  isUploading: boolean;
  startUpload: (
    files: File[],
    context: MediaContext,
    eventId: number,
    albumId?: number,
    onComplete?: (
      uploadedFiles: any[],
      summary?: UploadCompletionSummary
    ) => void
  ) => Promise<void>;
  cancelSession: (sessionId: string) => void;
  clearCompletedSessions: () => void;
  minimized: boolean;
  setMinimized: (minimized: boolean) => void;
  // Validation error state
  validationError: string | null;
  clearValidationError: () => void;
}

const UploadContext = createContext<UploadContextValue | null>(null);

// Default no-op implementation for SSR
const defaultUploadContext: UploadContextValue = {
  sessions: [],
  isUploading: false,
  startUpload: async () => {
    console.warn("[useUpload] startUpload called outside of UploadProvider");
  },
  cancelSession: () => {},
  clearCompletedSessions: () => {},
  minimized: false,
  setMinimized: () => {},
  validationError: null,
  clearValidationError: () => {},
};

export function useUpload() {
  const context = useContext(UploadContext);
  // Return default context during SSR or if provider is missing
  // This prevents hydration errors while still allowing client-side functionality
  if (!context) {
    return defaultUploadContext;
  }
  return context;
}

interface UploadProviderProps {
  children: React.ReactNode;
}

export function UploadProvider({ children }: UploadProviderProps) {
  const [sessions, setSessions] = useState<UploadSession[]>([]);
  const [minimized, setMinimized] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  // Buffer progress updates to avoid excessive re-renders when uploading many files
  const progressBufferRef = useRef<Map<string, Map<number, number>>>(new Map());
  const progressFlushTimerRef = useRef<number | null>(null);

  const isUploading = sessions.some((s) => s.status === "uploading");

  const clearValidationError = useCallback(() => {
    setValidationError(null);
  }, []);

  // Prevent page close/navigation during upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue =
          "Upload in progress. Are you sure you want to leave? Your uploads will be lost.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isUploading]);

  const generateId = () =>
    `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const bufferFileProgress = useCallback(
    (sessionId: string, fileIndex: number, progress: number) => {
      const clamped = Math.max(0, Math.min(100, progress));
      const bySession =
        progressBufferRef.current.get(sessionId) ?? new Map<number, number>();
      bySession.set(fileIndex, clamped);
      progressBufferRef.current.set(sessionId, bySession);

      // Flush at most every ~100ms
      if (progressFlushTimerRef.current != null) return;
      progressFlushTimerRef.current = window.setTimeout(() => {
        const sessionUpdates = progressBufferRef.current;
        progressBufferRef.current = new Map();
        progressFlushTimerRef.current = null;

        setSessions((prev) =>
          prev.map((s) => {
            const updates = sessionUpdates.get(s.id);
            if (!updates || updates.size === 0) return s;
            return {
              ...s,
              files: s.files.map((f, idx) => {
                const next = updates.get(idx);
                return next == null ? f : { ...f, progress: next };
              }),
            };
          })
        );
      }, 100);
    },
    []
  );

  const uploadToSignedUrlWithProgress = useCallback(
    async (
      file: File,
      signedUrl: string,
      abortSignal: AbortSignal,
      onProgress: (loaded: number, total: number) => void
    ) => {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedUrl, true);

        // Match the prior fetch() headers (best-effort; CORS may restrict setting headers)
        if (file.type) {
          try {
            xhr.setRequestHeader("Content-Type", file.type);
          } catch {
            // ignore
          }
        }
        try {
          xhr.setRequestHeader("Cache-Control", "3600");
        } catch {
          // ignore
        }

        const handleAbort = () => {
          try {
            xhr.abort();
          } catch {
            // ignore
          }
        };
        abortSignal.addEventListener("abort", handleAbort, { once: true });

        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) return;
          onProgress(e.loaded, e.total);
        };

        xhr.onload = () => {
          abortSignal.removeEventListener("abort", handleAbort);
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error("Storage upload failed"));
        };
        xhr.onerror = () => {
          abortSignal.removeEventListener("abort", handleAbort);
          reject(new Error("Storage upload failed"));
        };
        xhr.onabort = () => {
          abortSignal.removeEventListener("abort", handleAbort);
          const err = new Error("Aborted");
          (err as any).name = "AbortError";
          reject(err);
        };

        try {
          xhr.send(file);
        } catch (e) {
          abortSignal.removeEventListener("abort", handleAbort);
          reject(e);
        }
      });
    },
    []
  );

  const mapLimit = useCallback(
    async <T, R>(
      items: T[],
      concurrency: number,
      worker: (item: T, index: number) => Promise<R>
    ): Promise<PromiseSettledResult<R>[]> => {
      const results: PromiseSettledResult<R>[] = new Array(items.length);
      let nextIndex = 0;

      const runWorker = async () => {
        while (true) {
          const current = nextIndex++;
          if (current >= items.length) return;
          try {
            const value = await worker(items[current], current);
            results[current] = { status: "fulfilled", value };
          } catch (reason) {
            results[current] = { status: "rejected", reason };
          }
        }
      };

      const threads = Array.from(
        { length: Math.max(1, Math.min(concurrency, items.length)) },
        () => runWorker()
      );
      await Promise.all(threads);
      return results;
    },
    []
  );

  const validateFiles = useCallback(
    async (selectedFiles: File[], role: string): Promise<string | null> => {
      if (selectedFiles.length > UPLOAD_SETTINGS.maxFiles) {
        return `Cannot upload more than ${UPLOAD_SETTINGS.maxFiles} files at once`;
      }

      const config =
        MEDIA_CONFIGS[role as keyof typeof MEDIA_CONFIGS] ||
        MEDIA_CONFIGS.contractor;

      // Fast synchronous checks first
      for (const file of selectedFiles) {
        if (file.size > (config.maxFileSize || UPLOAD_SETTINGS.maxFileSize)) {
          const maxSizeMB = Math.round(
            (config.maxFileSize || UPLOAD_SETTINGS.maxFileSize) / (1024 * 1024)
          );
          return `File "${file.name}" is too large. Maximum size is ${maxSizeMB}MB`;
        }

        if (!UPLOAD_SETTINGS.allowedTypes.includes(file.type)) {
          return `Only JPEG and PNG image files are supported.`;
        }
      }

      // Magic-bytes validation (limit concurrency so selecting 1000 files doesn't freeze the UI)
      const validationConcurrency = 20;
      const results = await mapLimit(
        selectedFiles,
        validationConcurrency,
        async (file) => validateFileType(file)
      );
      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        if (r.status === "rejected") {
          return `File "${selectedFiles[i].name}": Validation failed`;
        }
        if (!r.value.valid) {
          return `File "${selectedFiles[i].name}": ${r.value.error}`;
        }
      }

      return null;
    },
    [mapLimit]
  );

  const uploadBatch = useCallback(
    async (
      batch: File[],
      context: MediaContext,
      eventId: number,
      albumId: number | undefined,
      sessionId: string,
      startIndex: number,
      abortSignal: AbortSignal
    ): Promise<any[]> => {
      // Update files to preparing
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                files: s.files.map((f, idx) =>
                  idx >= startIndex && idx < startIndex + batch.length
                    ? { ...f, status: "preparing" as const, progress: 10 }
                    : f
                ),
              }
            : s
        )
      );

      // Step 1: Get signed URLs (using the same format as mediaService.uploadMedia)
      const urlResponse = await fetch("/api/media/upload-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          eventId,
          partnerId: context.partnerId,
          role: context.role,
          fileCount: batch.length,
          fileTypes: batch.map((f) => f.type || undefined),
          ...(albumId ? { albumId } : {}),
        }),
        signal: abortSignal,
      });

      if (!urlResponse.ok) {
        const errorData = await urlResponse.json().catch(() => ({}));
        console.error("[UploadProvider] Failed to get upload URLs:", errorData);
        throw new Error(
          errorData.error || errorData.detail || "Failed to get upload URLs"
        );
      }

      const responseData = await urlResponse.json();
      const { signedUrls, filePaths } = responseData;

      if (
        !signedUrls ||
        !Array.isArray(signedUrls) ||
        signedUrls.length === 0
      ) {
        console.error(
          "[UploadProvider] Invalid upload URLs response:",
          responseData
        );
        throw new Error("Invalid response from upload URL API");
      }

      // Step 2: Upload files to storage (parallel PUTs)
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                files: s.files.map((f, idx) =>
                  idx >= startIndex && idx < startIndex + batch.length
                    ? { ...f, status: "uploading" as const, progress: 20 }
                    : f
                ),
              }
            : s
        )
      );

      type UploadPayload = {
        filePath: string;
        fileName: string;
        fileSize: number;
        fileType: string;
        globalIdx: number;
      };

      // Uploads: limit concurrency so a 1000-file batch doesn't spawn 1000 parallel connections
      const uploadConcurrency = 10;
      const uploadResults = await mapLimit(
        batch,
        uploadConcurrency,
        async (file, batchIdx): Promise<UploadPayload> => {
          const globalIdx = startIndex + batchIdx;
          const signedUrl = signedUrls[batchIdx];
          const filePath = filePaths[batchIdx];

          if (!signedUrl || !filePath) {
            throw new Error(`Missing upload info for file ${file.name}`);
          }

          // Keep the upload stage in roughly 20..79%, leaving 80% for "creating_record"
          await uploadToSignedUrlWithProgress(
            file,
            signedUrl,
            abortSignal,
            (loaded, total) => {
              const ratio = total > 0 ? loaded / total : 0;
              const pct = 20 + Math.round(ratio * 59); // 20..79
              bufferFileProgress(sessionId, globalIdx, pct);
            }
          );

          return {
            filePath,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            globalIdx,
          };
        }
      );

      const failed = uploadResults
        .map((result, batchIdx) => ({ result, batchIdx }))
        .filter(({ result }) => result.status === "rejected");

      if (failed.length > 0) {
        setSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  files: s.files.map((f, idx) => {
                    const failedEntry = failed.find(
                      ({ batchIdx }) => startIndex + batchIdx === idx
                    );
                    if (!failedEntry) {
                      return f;
                    }
                    const reason =
                      failedEntry.result.status === "rejected"
                        ? failedEntry.result.reason
                        : null;
                    return {
                      ...f,
                      status: "failed" as const,
                      error:
                        reason instanceof Error
                          ? reason.message
                          : "Upload failed",
                    };
                  }),
                }
              : s
          )
        );
      }

      const successfulUploads: UploadPayload[] = uploadResults
        .filter(
          (r): r is PromiseFulfilledResult<UploadPayload> =>
            r.status === "fulfilled"
        )
        .map((r) => r.value);

      if (successfulUploads.length === 0) {
        return [];
      }

      // Step 3: Create database records.
      // With large batches (e.g. 1000), creating records in a single request can take long
      // enough to appear "stuck" at 80%. Chunk this step so per-file completion updates
      // stream through as chunks finish.
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                files: s.files.map((f, idx) =>
                  successfulUploads.some((u) => u.globalIdx === idx)
                    ? { ...f, status: "creating_record" as const, progress: 80 }
                    : f
                ),
              }
            : s
        )
      );

      const createdAll: any[] = [];
      const createRecordChunkSize = 200;
      const createRecordTimeoutMs = 60_000;

      for (
        let offset = 0;
        offset < successfulUploads.length;
        offset += createRecordChunkSize
      ) {
        const chunk = successfulUploads.slice(
          offset,
          offset + createRecordChunkSize
        );
        const chunkIdxs = chunk.map((u) => u.globalIdx);

        const controller = new AbortController();
        const onAbort = () => controller.abort();
        abortSignal.addEventListener("abort", onAbort, { once: true });
        const timer = window.setTimeout(
          () => controller.abort(),
          createRecordTimeoutMs
        );

        try {
          const recordResponse = await fetch("/api/media/create-record", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              eventId,
              partnerId: context.partnerId,
              albumId,
              role: context.role,
              uploads: chunk.map(
                ({ filePath, fileName, fileSize, fileType }) => ({
                  filePath,
                  fileName,
                  fileSize,
                  fileType,
                })
              ),
            }),
            signal: controller.signal,
          });

          if (!recordResponse.ok) {
            const errorData = await recordResponse.json().catch(() => ({}));
            console.error("[UploadProvider] Create record failed:", {
              status: recordResponse.status,
              errorData,
            });
            const message =
              errorData.detail ||
              errorData.error ||
              `Failed to create media records (HTTP ${recordResponse.status})`;

            setSessions((prev) =>
              prev.map((s) =>
                s.id === sessionId
                  ? {
                      ...s,
                      files: s.files.map((f, idx) =>
                        chunkIdxs.includes(idx)
                          ? { ...f, status: "failed" as const, error: message }
                          : f
                      ),
                    }
                  : s
              )
            );

            continue;
          }

          const recordData = await recordResponse.json().catch(() => ({}));
          const created = Array.isArray((recordData as any).files)
            ? (recordData as any).files
            : [];
          createdAll.push(...created);

          // Mark chunk as completed as soon as records are created
          setSessions((prev) =>
            prev.map((s) =>
              s.id === sessionId
                ? {
                    ...s,
                    files: s.files.map((f, idx) =>
                      chunkIdxs.includes(idx)
                        ? { ...f, status: "completed" as const, progress: 100 }
                        : f
                    ),
                  }
                : s
            )
          );
        } catch (error) {
          const message =
            error instanceof Error && error.name === "AbortError"
              ? "Create record request timed out or was cancelled"
              : error instanceof Error
              ? error.message
              : "Failed to create media records";

          setSessions((prev) =>
            prev.map((s) =>
              s.id === sessionId
                ? {
                    ...s,
                    files: s.files.map((f, idx) =>
                      chunkIdxs.includes(idx)
                        ? { ...f, status: "failed" as const, error: message }
                        : f
                    ),
                  }
                : s
            )
          );
        } finally {
          window.clearTimeout(timer);
          abortSignal.removeEventListener("abort", onAbort);
        }
      }

      return createdAll;
    },
    [bufferFileProgress, mapLimit, uploadToSignedUrlWithProgress]
  );

  const startUpload = useCallback(
    async (
      files: File[],
      context: MediaContext,
      eventId: number,
      albumId?: number,
      onComplete?: (
        uploadedFiles: any[],
        summary?: UploadCompletionSummary
      ) => void
    ) => {
      // Validate files
      const validationErrorMsg = await validateFiles(files, context.role);
      if (validationErrorMsg) {
        // Set the validation error state so UI can display it
        setValidationError(validationErrorMsg);
        throw new Error(validationErrorMsg);
      }

      const sessionId = generateId();
      const abortController = new AbortController();
      abortControllersRef.current.set(sessionId, abortController);

      // Create session
      const session: UploadSession = {
        id: sessionId,
        eventId,
        albumId,
        context,
        files: files.map((file) => ({
          id: generateId(),
          file,
          progress: 0,
          status: "pending",
        })),
        startedAt: new Date(),
        overallProgress: 0,
        status: "uploading",
      };

      console.log("[UploadProvider] Creating upload session:", {
        sessionId,
        fileCount: files.length,
        eventId,
        albumId,
      });

      setSessions((prev) => {
        const newSessions = [...prev, session];
        console.log("[UploadProvider] Sessions updated:", newSessions.length);
        return newSessions;
      });

      try {
        // Split into batches
        const batchSize = UPLOAD_SETTINGS.batchSize;
        const batches: File[][] = [];
        for (let i = 0; i < files.length; i += batchSize) {
          batches.push(files.slice(i, i + batchSize));
        }

        const allUploadedFiles: any[] = [];
        let completedFiles = 0;

        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          const batch = batches[batchIndex];
          const startIndex = batchIndex * batchSize;

          const batchResults = await uploadBatch(
            batch,
            context,
            eventId,
            albumId,
            sessionId,
            startIndex,
            abortController.signal
          );

          allUploadedFiles.push(...batchResults);
          completedFiles += batch.length;

          // Update overall progress
          const overallProgress = Math.round(
            (completedFiles / files.length) * 100
          );
          setSessions((prev) =>
            prev.map((s) =>
              s.id === sessionId ? { ...s, overallProgress } : s
            )
          );
        }

        // Determine final status
        const finalStatus =
          allUploadedFiles.length === files.length
            ? "completed"
            : allUploadedFiles.length > 0
            ? "partial"
            : "failed";

        setSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId
              ? { ...s, status: finalStatus, overallProgress: 100 }
              : s
          )
        );

        const summary: UploadCompletionSummary = {
          totalFiles: files.length,
          uploadedFiles: allUploadedFiles.length,
          failedFiles: Math.max(0, files.length - allUploadedFiles.length),
          status: finalStatus,
        };

        // Dispatch event for other components
        if (typeof window !== "undefined" && allUploadedFiles.length > 0) {
          window.dispatchEvent(
            new CustomEvent("media:uploaded", {
              detail: { files: allUploadedFiles, eventId, albumId },
            })
          );
        }

        onComplete?.(allUploadedFiles, summary);

        ApiLogger.info("[UPLOAD] Session completed", {
          sessionId,
          total: files.length,
          successful: allUploadedFiles.length,
          failed: summary.failedFiles,
          status: finalStatus,
        });
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          ApiLogger.info("[UPLOAD] Session cancelled", { sessionId });
        } else {
          ApiLogger.error("[UPLOAD] Session failed", {
            sessionId,
            error: error instanceof Error ? error.message : "Unknown error",
          });

          setSessions((prev) =>
            prev.map((s) =>
              s.id === sessionId ? { ...s, status: "failed" } : s
            )
          );
        }
      } finally {
        abortControllersRef.current.delete(sessionId);
      }
    },
    [validateFiles, uploadBatch]
  );

  const cancelSession = useCallback((sessionId: string) => {
    const controller = abortControllersRef.current.get(sessionId);
    if (controller) {
      controller.abort();
    }
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  const clearCompletedSessions = useCallback(() => {
    setSessions((prev) => prev.filter((s) => s.status === "uploading"));
  }, []);

  return (
    <UploadContext.Provider
      value={{
        sessions,
        isUploading,
        startUpload,
        cancelSession,
        clearCompletedSessions,
        minimized,
        setMinimized,
        validationError,
        clearValidationError,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
}
